import * as z from "zod/v4";
import { SlugSchema } from "./slug.ts";
import { TagsSchema } from "./tag.ts";
import { IsoDateTimeSchema } from "./iso.ts";
import { ResultSchemaOf } from "./result.ts";
import { nowIsoString } from "../util/date.ts";
import {
  type LibraryAttachment,
  LibraryAttachmentSchema,
} from "./library-attachment.ts";

const emptyArray = <T>(): T[] => [];

export const LibraryNoteSchema = z.object({
  slug: SlugSchema.describe(
    "Globally unique slug (lowercase word characters and dashes)",
  ),
  type: SlugSchema.describe(
    `A type discriminator for the library note (lowercase word characters and dashes).`,
  ).default("note"),
  title: z.string(),
  content: z.string(),
  meta: z.json().describe("JSON metadata"),
  tags: TagsSchema.default(emptyArray),
  source_slugs: z
    .array(SlugSchema)
    .default(emptyArray)
    .describe(
      `If this note was derived from other notes, include their slugs here (provenance)`,
    ),
  source_urls: z
    .array(z.url())
    .default(emptyArray)
    .describe(
      `If this note was derived from any URLs, include them here (provenance)`,
    ),
  confidence: z
    .number()
    .min(0)
    .max(1)
    .default(0.5)
    .describe(
      "Confidence level in the quality or factuality of this note (0..1)",
    ),
  created_at: IsoDateTimeSchema.default(nowIsoString),
  updated_at: IsoDateTimeSchema.default(nowIsoString),
});

export type LibraryNote = z.infer<typeof LibraryNoteSchema>;

export const LibraryNoteArraySchema = z.array(LibraryNoteSchema);
export type LibraryNoteArray = z.infer<typeof LibraryNoteArraySchema>;

export const LibraryNoteOptionSchema = LibraryNoteSchema.optional();
export type LibraryNoteOption = z.infer<typeof LibraryNoteOptionSchema>;

export const LibraryNoteResultSchema = ResultSchemaOf(
  LibraryNoteSchema,
  z.string(),
);
export type LibraryNoteResult = z.infer<typeof LibraryNoteResultSchema>;

export const LibraryNoteArrayResultSchema = ResultSchemaOf(
  z.array(LibraryNoteSchema),
  z.string(),
);
export type LibraryNoteArrayResult = z.infer<
  typeof LibraryNoteArrayResultSchema
>;

export const LibraryNoteTeaserSchema = LibraryNoteSchema.omit({
  content: true,
  meta: true,
  source_slugs: true,
  source_urls: true,
}).extend({
  excerpt: z.string(),
  attachments: z.array(LibraryAttachmentSchema).optional(),
});

export type LibraryNoteTeaser = z.infer<typeof LibraryNoteTeaserSchema>;
export const LibraryNoteTeaserArraySchema = z.array(LibraryNoteTeaserSchema);
export type LibraryNoteTeaserArray = z.infer<
  typeof LibraryNoteTeaserArraySchema
>;

export const LibraryNoteTeaserArrayResultSchema = ResultSchemaOf(
  LibraryNoteTeaserArraySchema,
  z.string(),
);
export type LibraryNoteTeaserArrayResult = z.infer<
  typeof LibraryNoteTeaserArrayResultSchema
>;

/** Lightweight library note item for caching in frontend store */
export type LibraryNoteItem = {
  slug: z.infer<typeof SlugSchema>;
  type: string;
  title: string;
  excerpt: string;
  tags: string[];
  attachments?: LibraryAttachment[];
};

export const LibraryNoteOptionResultSchema = ResultSchemaOf(
  LibraryNoteOptionSchema,
  z.string(),
);
export type LibraryNoteOptionResult = z.infer<
  typeof LibraryNoteOptionResultSchema
>;
